// third party libs
import Typography from '@navi/web-ui/lib/primitives/Typography/index';
import Button from '@navi/web-ui/lib/primitives/Button/index';
import React, { PropsWithChildren, useCallback, useMemo, useReducer } from 'react';
import cx from 'classnames';
import CloseIcon from '@navi/web-ui/lib/icons/CloseIcon';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
// styles
import styles from './index.module.scss';
// components and functions
import Overlay from 'src/components/Overlay/Overlay';
import { calculateOverDue } from '../../actions/casesDetailsActions';
import {
  CalculateOverdueAction,
  ICalculateOverdue,
  OverdueResponse
} from '../../interfaces/EmiSchedule.type';
import { CalculateOverdueKind, OverdueMappings } from '../../constants/EmiSchedule.constant';
import { formatAmount } from 'src/utils/commonUtils';
import { DateFormat, dateFormat, getFormatDate } from 'src/utils/DateHelper';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import Modal from '@cp/src/components/Modal/Modal';
import DateTimePickerComponent from '../../../../components/DateTimePicker/DateTimePickerComponent';
import { DATE_TIME_TYPE } from '../../../../components/DateTimePicker/constants';

const enachSetupReducer = (state: ICalculateOverdue, action: CalculateOverdueAction) => {
  const { type, payload } = action;
  switch (type) {
    case CalculateOverdueKind.INITIAL_STATE:
      return {
        ...state,
        open: true
      };
    case CalculateOverdueKind.CLOSE:
      return {
        ...state,
        open: false
      };
    case CalculateOverdueKind.BEFORE_API_CALL:
      return {
        ...state,
        loading: true
      };
    case CalculateOverdueKind.API_CALL_SUCCESS:
      return {
        ...state,
        loading: false,
        expand: true,
        overdueData: payload?.overdueData || null,
        overdueOn: payload?.overdueOnData || null
      };
    case CalculateOverdueKind.SET_DATE:
      return {
        ...state,
        date: payload?.date || '',
        expand: false,
        overdueData: null,
        overdueOn: null
      };
    case CalculateOverdueKind.API_ERROR:
      return {
        ...state,
        loading: false
      };
    default:
      return state;
  }
};

const today = dateFormat(new Date(), DateFormat.ISO_DATE_FORMAT);

const CalculateOverDue: React.FC<PropsWithChildren> = () => {
  const { loanId = '' } = useParams();
  const dispatch = useDispatch();
  const [overDue, dispatchOverDue] = useReducer(enachSetupReducer, {
    open: false,
    loading: false,
    date: '',
    expand: false,
    overdueData: null,
    overdueOn: null
  });
  const openModal = useCallback(() => {
    dispatchOverDue({ type: CalculateOverdueKind.INITIAL_STATE });
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_EMISchedule_CalculateOverdue);
  }, []);

  const closeModal = useCallback(() => {
    dispatchOverDue({ type: CalculateOverdueKind.CLOSE });
  }, []);

  const updateDate = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    dispatchOverDue({
      type: CalculateOverdueKind.SET_DATE,
      payload: { date: event.target.value }
    });
  }, []);
  const successCallback = useCallback(
    (data: OverdueResponse) => {
      const overdueData: Array<JSX.Element> = [];
      let totalOverdue = 0;
      const keys = Object.keys(OverdueMappings) as (keyof OverdueResponse)[];
      keys.forEach(key => {
        totalOverdue += data?.[key as keyof OverdueResponse]?.value;
        if (data[key]?.value || data[key]?.value >= 0) {
          overdueData.push(
            <div key={key} className={styles.overdue}>
              <Typography className={styles.dim} as={'p'} variant={'p4'}>
                {OverdueMappings[key]}
              </Typography>
              <Typography className={styles.redColor} as={'p'} variant={'p3'}>
                {formatAmount(data[key]?.value, false)}
              </Typography>
            </div>
          );
        }
      });

      const overdueOnData = (
        <div className={styles.overdue}>
          <Typography className={styles.dim} as={'p'} variant={'p4'}>
            Overdue on {getFormatDate(overDue.date, DateFormat.DD_MMM)}
          </Typography>
          <Typography className={styles.dark} as={'p'} variant={'p3'}>
            {formatAmount(totalOverdue, false)}
          </Typography>
        </div>
      );
      dispatchOverDue({
        type: CalculateOverdueKind.API_CALL_SUCCESS,
        payload: { overdueData, overdueOnData }
      });
    },
    [overDue.date]
  );

  const calculateOverdue = useCallback(() => {
    dispatchOverDue({ type: CalculateOverdueKind.BEFORE_API_CALL });
    dispatch(calculateOverDue(loanId, overDue.date, successCallback, failureCallback));
  }, [overDue.date]);

  const failureCallback = useCallback(() => {
    dispatchOverDue({ type: CalculateOverdueKind.API_ERROR });
  }, [overDue.date]);

  return (
    <div className={styles.calculateOverDue}>
      <Button onClick={openModal} variant={'text'}>
        Calculate Overdue
      </Button>
      <Modal visible={overDue.open} onClose={closeModal} header={'Calculate overdue payment'}>
        <div className={cx([styles.overDueCard, { [styles.height]: overDue.expand }])}>
          <Typography className={styles.enterDate} variant={'p4'}>
            Select payment date
          </Typography>
          <DateTimePickerComponent
            type={DATE_TIME_TYPE.DATE}
            min={today}
            value={overDue.date}
            containerClasses={styles.overdueDatePicker}
            inputClasses={styles.overdueDatePickerInput}
            onChange={updateDate}
          />
          <data>
            {overDue?.overdueOn}
            {overDue?.overdueData}
          </data>
          {!overDue.overdueData && (
            <Button onClick={calculateOverdue} disabled={!overDue.date || overDue.loading}>
              {overDue.loading ? 'Calculating ...' : 'Calculate'}
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CalculateOverDue;
